import { useId, type ReactNode } from "react";
import { Button, Icon } from "@omnabase/ui";
import { isFormBlock } from "@omnabase/shared";
import type { DraftBlock, DraftField, DraftInput } from "../../../lib/formEdits";

/** The builder's preview (builder v2, the founder's design; round one drew it as a third card): the region "Preview" with its top bar (the
 *  Desktop/Mobile switch, and on a wide screen "Unsaved changes", Cancel and Save) and the form as the customer meets it, 620 px wide on
 *  Desktop and 380 px on Mobile: every item in the builder's order (a Text block as the real heading or paragraph it is on the page, the text
 *  blocks round, question 11), the required fields marked, the help lines, the disabled button with the form's own word. A click on an item
 *  opens its row on the left (a mouse shortcut; the keyboard's way is the list), so the preview's controls stay out of the tab order. A hidden
 *  field shows as a quiet line. Nothing here is sent anywhere. */
export type PreviewMode = "desktop" | "mobile";

export function PreviewPane({ items, submit, mode, onMode, actions, open, onOpenField, paneRef }: {
  items: readonly DraftField[]; /** the button's word: the form's own, or the default */ submit: string; mode: PreviewMode; onMode: (m: PreviewMode) => void;
  /** Unsaved changes, Cancel and Save, when they live in this bar (two columns); null when the phone's bar at the foot holds them */ actions: ReactNode;
  open: string | null; onOpenField: (uid: string) => void; paneRef?: (el: HTMLElement | null) => void;
}) {
  return (
    <section className="ob-form-builder__preview" aria-label="Preview" ref={paneRef}>
      <div className="ob-form-builder__preview-bar">
        <div className="ob-form-builder__modes">
          <ModeButton icon="device" label="Desktop" on={mode === "desktop"} onClick={() => onMode("desktop")} />
          <ModeButton icon="smartphone" label="Mobile" on={mode === "mobile"} onClick={() => onMode("mobile")} />
        </div>
        {actions}
      </div>
      <div className="ob-form-builder__preview-body">
        <form className={`ob-form-preview ob-form-preview--${mode}`} onSubmit={(e) => e.preventDefault()} noValidate>
          {items.map((f) => <PreviewField key={f.uid} field={f} open={f.uid === open} onOpen={() => onOpenField(f.uid)} />)}
          <div className="ob-form-preview__submit"><Button variant="primary" type="submit" disabled>{submit}</Button></div>
        </form>
      </div>
    </section>
  );
}

function ModeButton({ icon, label, on, onClick }: { icon: "device" | "smartphone"; label: string; on: boolean; onClick: () => void }) {
  return <button type="button" className="ob-form-builder__mode" aria-pressed={on} onClick={onClick}><Icon name={icon} size="sm" />{label}</button>;
}

/** the shell every item sits in: the preview takes no input, a click leads to the item's row */
function PreviewShell({ open, onOpen, children }: { open: boolean; onOpen: () => void; children: ReactNode }) {
  return <div className={`ob-form-preview__field${open ? " ob-form-preview__field--open" : ""}`} onClick={(e) => { e.preventDefault(); onOpen(); }}>{children}</div>;
}

function PreviewField({ field: f, open, onOpen }: { field: DraftField; open: boolean; onOpen: () => void }) {
  return isFormBlock(f) ? <PreviewShell open={open} onOpen={onOpen}><PreviewBlock block={f} /></PreviewShell> : <PreviewInput field={f} open={open} onOpen={onOpen} />;
}
/** a Text block as the element it is drawn as on the page, its words as text (line breaks kept by the stylesheet) */
function PreviewBlock({ block: b }: { block: DraftBlock }) {
  const Tag = b.as;
  return <Tag className={`ob-form-preview__block ob-form-preview__block--${b.as}`}>{b.text}</Tag>;
}
function PreviewInput({ field: f, open, onOpen }: { field: DraftInput; open: boolean; onOpen: () => void }) {
  const id = useId(); const help = f.help ? `${id}-help` : undefined;
  const label = f.label.trim() || "Untitled field";
  const shell = (children: ReactNode) => <PreviewShell open={open} onOpen={onOpen}>{children}</PreviewShell>;
  if (f.type === "hidden") return shell(<p className="ob-form-preview__hidden">Hidden: {label}{f.hiddenValue ? ` (${f.hiddenValue})` : ""}</p>);
  const mark = f.required ? <span className="ob-form-preview__required" aria-hidden="true"> *</span> : null;
  const helpLine = f.help ? <p className="ob-form-preview__help" id={help}>{f.help}</p> : null;
  if (f.type === "checkbox") {
    return shell(<><label className="ob-form-preview__check"><input type="checkbox" tabIndex={-1} required={f.required} aria-describedby={help} /><span><span>{label}</span>{mark}</span></label>{helpLine}</>);
  }
  return shell(<><label className="ob-field-label" htmlFor={id}><span>{label}</span>{mark}</label><PreviewControl field={f} id={id} help={help} />{helpLine}</>);
}
function PreviewControl({ field: f, id, help }: { field: DraftInput; id: string; help?: string }) {
  const common = { id, required: f.required, "aria-describedby": help, tabIndex: -1 };
  if (f.type === "textarea") return <textarea className="ob-textarea ob-form-preview__textarea" placeholder={f.placeholder} readOnly {...common} />;
  if (f.type === "select") return <select className="ob-select" defaultValue="" {...common}><option value="" disabled>{f.placeholder || "Choose…"}</option>{(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
  if (f.type === "file") return <input type="file" className="ob-form-preview__file" {...common} />;
  return <span className="ob-input ob-input--text"><input className="ob-input__field" type={f.type === "email" ? "email" : "text"} placeholder={f.placeholder} readOnly {...common} /></span>;
}
